import React, { useState } from 'react';
import { supabase } from './supabaseClient';
import FeatureIcons from './components/FeatureIcons';

export default function MarketingPage({ onEnterApp, onSelectPlan }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [showWhyManifold, setShowWhyManifold] = useState(false);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const { error } = await supabase
        .from('waitlist')
        .insert({ email: email.trim().toLowerCase() });

      if (error) {
        if (error.code === '23505') {
          setStatus('success');
        } else {
          throw error;
        }
      } else {
        setStatus('success');
      }
    } catch (err) {
      console.error('Waitlist signup error:', err);
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  const features = [
    {
      title: 'Know before you apply',
      description: 'See how well you match and what\'s missing in seconds',
      icon: FeatureIcons.matchScore,
    },
    {
      title: 'One resume won\'t cut it',
      description: 'Get a tailored version optimized for every role',
      icon: FeatureIcons.tailoredResume,
    },
    {
      title: 'Never lose track',
      description: 'Every app, status, and next step in one place',
      icon: FeatureIcons.opportunityTracker,
    },
    {
      title: 'Turn rejections into growth',
      description: 'Learn exactly what to improve after every interview',
      icon: FeatureIcons.growthFeedback,
    },
    {
      title: 'Close the gap, fast',
      description: 'Personalized projects that build the skills employers want',
      icon: FeatureIcons.bridgeGap,
      tier: 'pro',
    },
    {
      title: 'Walk in ready',
      description: 'Practice with questions tailored to the exact role',
      icon: FeatureIcons.walkInReady,
      tier: 'pro',
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #fffef9 0%, #f5f0e6 100%)',
      fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      position: 'relative',
    }}>
      <style>{`
        .marketing-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image:
            /* Main grid lines */
            linear-gradient(rgba(201, 168, 124, 0.12) 1px, transparent 1px),
            linear-gradient(90deg, rgba(201, 168, 124, 0.12) 1px, transparent 1px),
            /* Larger accent grid */
            linear-gradient(rgba(201, 168, 124, 0.06) 2px, transparent 2px),
            linear-gradient(90deg, rgba(201, 168, 124, 0.06) 2px, transparent 2px),
            /* Career trajectory curves */
            radial-gradient(ellipse 800px 400px at 85% 90%, rgba(212, 115, 106, 0.06) 0%, transparent 60%),
            radial-gradient(ellipse 600px 300px at 15% 70%, rgba(247, 201, 72, 0.06) 0%, transparent 60%);
          background-size:
            32px 32px,
            32px 32px,
            160px 160px,
            160px 160px,
            100% 100%,
            100% 100%;
          pointer-events: none;
          z-index: 0;
        }
        .marketing-bg::before {
          content: '';
          position: absolute;
          top: 150px;
          right: 80px;
          width: 140px;
          height: 140px;
          border: 2px dashed rgba(201, 168, 124, 0.2);
          border-radius: 50%;
          z-index: 0;
        }
        .marketing-bg::after {
          content: '';
          position: absolute;
          bottom: 200px;
          left: 60px;
          width: 100px;
          height: 100px;
          border: 2px dashed rgba(247, 201, 72, 0.15);
          transform: rotate(45deg);
          z-index: 0;
        }
        .marketing-content {
          position: relative;
          z-index: 1;
        }
        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        .pricing-grid {
          display: flex;
          justify-content: center;
          gap: 16px;
        }
        @media (max-width: 768px) {
          .section-title {
            font-size: 19px !important;
          }
          .features-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 10px !important;
          }
          .features-grid > div {
            padding: 12px 8px !important;
          }
          .features-grid h3 {
            font-size: 12px !important;
          }
          .features-grid p {
            font-size: 10px !important;
            line-height: 1.3 !important;
          }
          .features-grid svg {
            width: 28px !important;
            height: 28px !important;
          }
          .pricing-grid > div {
            padding: 24px 20px !important;
            max-width: 340px;
          }
          .pricing-grid .alpha-badge {
            font-size: 10px !important;
          }
          .pricing-grid .price-amount {
            font-size: 32px !important;
          }
          .pricing-grid ul {
            font-size: 13px !important;
          }
          .pricing-grid button {
            padding: 12px !important;
            font-size: 13px !important;
          }
        }
        @media (max-width: 480px) {
          .section-title {
            font-size: 16px !important;
          }
          .features-grid {
            gap: 6px !important;
          }
          .features-grid > div {
            padding: 10px 6px !important;
          }
          .features-grid h3 {
            font-size: 10px !important;
          }
          .features-grid p {
            font-size: 9px !important;
          }
          .features-grid svg {
            width: 24px !important;
            height: 24px !important;
          }
          .pricing-grid > div {
            padding: 20px 16px !important;
            max-width: 100%;
          }
          .pricing-grid .price-amount {
            font-size: 28px !important;
          }
          .pricing-grid ul {
            font-size: 12px !important;
          }
          .pricing-grid button {
            padding: 10px !important;
            font-size: 12px !important;
          }
        }
      `}</style>
      <div className="marketing-bg"></div>
      <div className="marketing-content">
      {/* Hero Section - Compact */}
      <div style={{
        padding: '32px 20px',
        textAlign: 'center',
        maxWidth: '700px',
        margin: '0 auto',
      }}>
        {/* Logo */}
        <svg width="60" height="60" viewBox="0 0 52 52" fill="none" style={{ marginBottom: '16px' }}>
          <path d="M8 38 Q16 28, 22 32 T34 26" stroke="#3d3d3d" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.8"/>
          <path d="M6 44 Q14 32, 20 38 T32 30" stroke="#3d3d3d" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.4"/>
          <circle cx="8" cy="38" r="2.5" fill="#d4736a"/>
          <g transform="rotate(-45 38 14)">
            <rect x="24" y="11" width="20" height="6" fill="#f7c948" stroke="#d4a832" strokeWidth="0.5"/>
            <rect x="44" y="11" width="4" height="6" fill="#b8b8b8"/>
            <rect x="48" y="11.5" width="3" height="5" rx="0.5" fill="#d4736a"/>
            <polygon points="24,11 24,17 20,14" fill="#c9a87c"/>
            <polygon points="20,14 22,12.5 22,15.5" fill="#3d3d3d"/>
          </g>
        </svg>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          margin: '0 0 12px 0',
          position: 'relative',
        }}>
          <p style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#7a7a7a',
            margin: 0,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}>
            Manifold Careers
          </p>
          <button
            onClick={() => setShowWhyManifold(!showWhyManifold)}
            style={{
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              border: '1.5px solid #c9a87c',
              background: 'transparent',
              color: '#c9a87c',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#c9a87c';
              e.target.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#c9a87c';
            }}
            aria-label="Why Manifold?"
          >
            ?
          </button>

          {/* Why Manifold tooltip */}
          {showWhyManifold && (
            <div style={{
              position: 'absolute',
              top: '32px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'white',
              borderRadius: '12px',
              padding: '20px 24px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              border: '1px solid #e8e8e8',
              maxWidth: '360px',
              width: '90vw',
              zIndex: 100,
              textAlign: 'left',
            }}>
              {/* Close button */}
              <button
                onClick={() => setShowWhyManifold(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  color: '#999',
                  cursor: 'pointer',
                  padding: '4px',
                  lineHeight: 1,
                }}
              >
                ×
              </button>

              <h4 style={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#2d2d2d',
                margin: '0 0 12px 0',
              }}>
                Why "Manifold"?
              </h4>

              <p style={{
                fontSize: '13px',
                color: '#5a5a5a',
                margin: '0 0 12px 0',
                lineHeight: '1.6',
              }}>
                In math, a <em>manifold</em> is a complex space that looks simple when you zoom in close enough. Your career works the same way — from a distance it feels overwhelming, but with the right perspective, every next step becomes clear.
              </p>

              <p style={{
                fontSize: '13px',
                color: '#5a5a5a',
                margin: '0 0 12px 0',
                lineHeight: '1.6',
              }}>
                We break the complexity of job searching, skill-building, and career planning into clear, actionable steps — so the path forward always makes sense.
              </p>

              <p style={{
                fontSize: '13px',
                color: '#c9a87c',
                margin: 0,
                fontWeight: '500',
                fontStyle: 'italic',
              }}>
                Your career isn't a straight line. Manifold helps you navigate the curves.
              </p>
            </div>
          )}
        </div>

        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#2d2d2d',
          margin: '0 0 16px 0',
          letterSpacing: '-0.5px',
          lineHeight: '1.2',
        }}>
          Land more interviews.<br />Get hired faster.
        </h1>

        {/* Problem statement */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '24px',
          marginBottom: '16px',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '13px', color: '#9ca3af' }}>
            <span style={{ color: '#d4736a' }}>✗</span> Job apps are time-consuming
          </span>
          <span style={{ fontSize: '13px', color: '#9ca3af' }}>
            <span style={{ color: '#d4736a' }}>✗</span> Generic resumes get filtered
          </span>
          <span style={{ fontSize: '13px', color: '#9ca3af' }}>
            <span style={{ color: '#d4736a' }}>✗</span> Hard to know if you qualify
          </span>
        </div>

        <p style={{
          fontSize: '15px',
          color: '#5a5a5a',
          margin: '0 0 24px 0',
          lineHeight: '1.6',
          maxWidth: '520px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          <strong>See your match % instantly</strong>, get AI-optimized resumes in seconds, and know exactly what skills to build.
        </p>

        <button
          onClick={onEnterApp}
          style={{
            padding: '14px 32px',
            fontSize: '16px',
            fontWeight: '600',
            color: 'white',
            background: '#d4736a',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'background 0.2s, transform 0.1s',
          }}
          onMouseOver={(e) => e.target.style.background = '#c4635a'}
          onMouseOut={(e) => e.target.style.background = '#d4736a'}
        >
          Get started free
        </button>
        <p style={{
          fontSize: '13px',
          color: '#7a7a7a',
          margin: '16px 0 0 0',
        }}>
          5 free AI-optimized resumes • No credit card required
        </p>
      </div>

      {/* Features Section */}
      <div style={{
        padding: '40px 20px',
        background: 'white',
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 className="section-title" style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#2d2d2d',
            textAlign: 'center',
            margin: '0 0 32px 0',
          }}>
            Everything you need to land the right role
          </h2>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} style={{
                padding: '20px',
                background: feature.tier === 'pro' ? '#fafafa' : '#fffef9',
                borderRadius: '12px',
                textAlign: 'center',
                border: feature.tier === 'pro' ? '1px dashed #e0e0e0' : '1px solid #f0f0f0',
                position: 'relative',
              }}>
                {feature.tier === 'pro' && (
                  <span style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#f7c948',
                    background: '#fffef0',
                    padding: '2px 6px',
                    borderRadius: '4px',
                  }}>
                    PRO
                  </span>
                )}
                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', opacity: feature.tier === 'pro' ? 0.7 : 1 }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#2d2d2d',
                  margin: '0 0 6px 0',
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: '#7a7a7a',
                  margin: 0,
                  lineHeight: '1.4',
                }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div style={{
        padding: '40px 20px',
        background: 'linear-gradient(135deg, #fffef9 0%, #f5f0e6 100%)',
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 className="section-title" style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#2d2d2d',
            margin: '0 0 8px 0',
            textAlign: 'center',
          }}>
            Alpha Exclusive
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#7a7a7a',
            margin: '0 0 28px 0',
            textAlign: 'center',
          }}>
            Be an early adopter. Lock in 50% off for 3 months.
          </p>

          <div className="pricing-grid">
            {/* Plus Tier */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '28px 32px',
              border: '2px solid #e8e8e8',
              position: 'relative',
              maxWidth: '340px',
              width: '100%',
            }}>
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#3d3d3d',
                color: 'white',
                fontSize: '11px',
                fontWeight: '600',
                padding: '5px 14px',
                borderRadius: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Plus
              </div>
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                marginTop: '8px',
              }}>
                <span className="price-amount" style={{
                  fontSize: '40px',
                  fontWeight: '700',
                  color: '#2d2d2d',
                }}>
                  $20
                </span>
                <span style={{ fontSize: '14px', fontWeight: '400', color: '#7a7a7a' }}>/mo</span>
              </div>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 24px 0',
                fontSize: '14px',
                color: '#5a5a5a',
              }}>
                <li style={{ padding: '6px 0' }}>✓ Match scores & gap detection</li>
                <li style={{ padding: '6px 0', fontWeight: '600' }}>✓ 100 tailored resumes/mo</li>
                <li style={{ padding: '6px 0' }}>✓ Application tracker</li>
                <li style={{ padding: '6px 0' }}>✓ Post-interview feedback</li>
                <li style={{ padding: '6px 0', color: '#bbb' }}>✗ Skill-building projects</li>
                <li style={{ padding: '6px 0', color: '#bbb' }}>✗ Interview prep & coaching</li>
              </ul>
              <button
                onClick={() => { onSelectPlan?.('plus'); onEnterApp(); }}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'white',
                  background: '#3d3d3d',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseOver={(e) => e.target.style.background = '#2d2d2d'}
                onMouseOut={(e) => e.target.style.background = '#3d3d3d'}
              >
                Get Plus
              </button>
            </div>

            {/* Pro/ALPHAVIP Tier */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px 40px',
              border: '3px solid #f7c948',
              position: 'relative',
              maxWidth: '380px',
              width: '100%',
            }}>
              <div className="alpha-badge" style={{
                position: 'absolute',
                top: '-14px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #f7c948 0%, #d4a832 100%)',
                color: '#2d2d2d',
                fontSize: '12px',
                fontWeight: '700',
                padding: '6px 16px',
                borderRadius: '14px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                boxShadow: '0 2px 8px rgba(247, 201, 72, 0.4)',
              }}>
                ALPHAVIP
              </div>
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                marginTop: '8px',
              }}>
                <span style={{
                  fontSize: '18px',
                  color: '#999',
                  textDecoration: 'line-through',
                  marginRight: '8px',
                }}>$50</span>
                <span className="price-amount" style={{
                  fontSize: '48px',
                  fontWeight: '700',
                  color: '#2d2d2d',
                }}>
                  $25
                </span>
                <span style={{ fontSize: '16px', fontWeight: '400', color: '#7a7a7a' }}>/mo</span>
              </div>
              <p style={{
                fontSize: '14px',
                color: '#d4736a',
                margin: '0 0 24px 0',
                textAlign: 'center',
                fontWeight: '600',
              }}>
                50% off for your first 3 months
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 28px 0',
                fontSize: '15px',
                color: '#5a5a5a',
              }}>
                <li style={{ padding: '8px 0' }}>✓ Match scores & gap detection</li>
                <li style={{ padding: '8px 0', fontWeight: '600' }}>✓ Unlimited tailored resumes</li>
                <li style={{ padding: '8px 0' }}>✓ Application tracker</li>
                <li style={{ padding: '8px 0' }}>✓ Post-interview feedback</li>
                <li style={{ padding: '8px 0', fontWeight: '600' }}>✓ Skill-building projects</li>
                <li style={{ padding: '8px 0', fontWeight: '600' }}>✓ Interview prep & coaching</li>
              </ul>
              <button
                onClick={() => { onSelectPlan?.('pro'); onEnterApp(); }}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#2d2d2d',
                  background: 'linear-gradient(135deg, #f7c948 0%, #e5b83a 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(247, 201, 72, 0.3)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(247, 201, 72, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(247, 201, 72, 0.3)';
                }}
              >
                Claim Your Alpha Access
              </button>
              <p style={{
                fontSize: '12px',
                color: '#7a7a7a',
                margin: '16px 0 0 0',
                textAlign: 'center',
              }}>
                or <span
                  onClick={onEnterApp}
                  style={{
                    color: '#d4736a',
                    cursor: 'pointer',
                    fontWeight: '600',
                    textDecoration: 'underline',
                  }}
                >try 5 free resumes</span> first
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Waitlist Section */}
      <div style={{
        padding: '60px 20px',
        background: 'white',
      }}>
        <div style={{ maxWidth: '480px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#2d2d2d',
            margin: '0 0 12px 0',
          }}>
            Stay updated
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#7a7a7a',
            margin: '0 0 24px 0',
          }}>
            Get notified about new features and updates
          </p>

          {status === 'success' ? (
            <div style={{
              background: '#e8f5e9',
              border: '1px solid #a5d6a7',
              borderRadius: '12px',
              padding: '20px',
            }}>
              <p style={{ color: '#2e7d32', fontSize: '14px', margin: 0, fontWeight: '500' }}>
                You're on the list! We'll keep you updated.
              </p>
            </div>
          ) : (
            <form onSubmit={handleWaitlistSubmit}>
              <div style={{
                display: 'flex',
                gap: '12px',
                flexDirection: 'column',
              }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  style={{
                    padding: '14px 18px',
                    fontSize: '14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '10px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#d4736a'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  style={{
                    padding: '14px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white',
                    background: status === 'loading' ? '#ccc' : '#2d2d2d',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                  }}
                >
                  {status === 'loading' ? 'Joining...' : 'Join Waitlist'}
                </button>
              </div>
              {status === 'error' && (
                <p style={{ color: '#d32f2f', fontSize: '14px', marginTop: '12px' }}>
                  {errorMsg}
                </p>
              )}
            </form>
          )}
        </div>
      </div>

      {/* Contact Footer */}
      <div style={{
        padding: '40px 20px',
        background: '#2d2d2d',
        textAlign: 'center',
      }}>
        <p style={{
          fontSize: '14px',
          color: '#b0b0b0',
          margin: '0 0 16px 0',
        }}>
          Questions or feedback? We'd love to hear from you.
        </p>
        <a
          href="mailto:loricorpuz@gmail.com?subject=Manifold Careers Feedback"
          style={{
            display: 'inline-block',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '500',
            color: 'white',
            background: '#d4736a',
            borderRadius: '8px',
            textDecoration: 'none',
          }}
        >
          Contact Us
        </a>
        <p style={{
          fontSize: '12px',
          color: '#7a7a7a',
          margin: '24px 0 0 0',
        }}>
          Manifold Careers - 2026
        </p>
      </div>
      </div>
    </div>
  );
}
